import { useState } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { toast } from 'sonner';
import { api } from '../utils/api';
import { generateDeviceFingerprint, getDeviceInfo } from '../utils/deviceFingerprint';
import { LogIn, Mail, Lock, Key } from 'lucide-react';
import { Checkbox } from '../components/ui/checkbox';

const Login = ({ onLogin }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [showOTP, setShowOTP] = useState(false);
  const [otp, setOtp] = useState('');
  const [trustDevice, setTrustDevice] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const deviceFingerprint = generateDeviceFingerprint();
      const deviceInfo = getDeviceInfo();

      const result = await api.login({
        email,
        password,
        device_fingerprint: deviceFingerprint,
        device_info: deviceInfo
      });

      if (result.require_otp) {
        toast.info('OTP sent to your email');
        setShowOTP(true);
      } else {
        toast.success('Login successful!');
        onLogin(result.token, result.user);
      }
    } catch (error) {
      toast.error(error.message || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOTP = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const deviceFingerprint = generateDeviceFingerprint();

      const result = await api.verifyOTP({
        email,
        otp,
        device_fingerprint: deviceFingerprint,
        trust_device: trustDevice
      });

      toast.success('Login successful!');
      onLogin(result.token, result.user);
    } catch (error) {
      toast.error(error.message || 'OTP verification failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md animate-fade-in">
        <div className="text-center mb-8">
          <h1 className="text-4xl sm:text-5xl font-bold text-slate-900 mb-3" data-testid="login-title">
            Welcome Back
          </h1>
          <p className="text-base text-slate-600">Sign in to access your dashboard</p>
        </div>

        <Card className="glass" data-testid="login-card">
          <CardHeader>
            <CardTitle>Login</CardTitle>
            <CardDescription>Enter your credentials to continue</CardDescription>
          </CardHeader>
          <CardContent>
            {!showOTP ? (
              <form onSubmit={handleLogin} className="space-y-4">
                <div>
                  <Label htmlFor="email">Email</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-3 w-4 h-4 text-slate-400" />
                    <Input
                      id="email"
                      type="email"
                      className="pl-10"
                      data-testid="email-input"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="password">Password</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-3 w-4 h-4 text-slate-400" />
                    <Input
                      id="password"
                      type="password"
                      className="pl-10"
                      data-testid="password-input"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                    />
                  </div>
                </div>
                <Button type="submit" className="w-full" disabled={loading} data-testid="login-btn">
                  {loading ? 'Signing in...' : 'Sign In'} <LogIn className="ml-2 w-4 h-4" />
                </Button>
              </form>
            ) : (
              <form onSubmit={handleVerifyOTP} className="space-y-4">
                <div>
                  <Label htmlFor="otp">Enter OTP</Label>
                  <div className="relative">
                    <Key className="absolute left-3 top-3 w-4 h-4 text-slate-400" />
                    <Input
                      id="otp"
                      type="text"
                      maxLength={6}
                      className="pl-10"
                      data-testid="otp-input"
                      value={otp}
                      onChange={(e) => setOtp(e.target.value)}
                      required
                    />
                  </div>
                  <p className="text-sm text-slate-500 mt-1">Check your email for the OTP code</p>
                </div>
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="trust"
                    checked={trustDevice}
                    onCheckedChange={setTrustDevice}
                    data-testid="trust-device-checkbox"
                  />
                  <Label htmlFor="trust" className="text-sm cursor-pointer">
                    Trust this device (skip OTP next time)
                  </Label>
                </div>
                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    className="flex-1"
                    onClick={() => setShowOTP(false)}
                    data-testid="back-to-login-btn"
                  >
                    Back
                  </Button>
                  <Button type="submit" className="flex-1" disabled={loading} data-testid="verify-otp-btn">
                    {loading ? 'Verifying...' : 'Verify OTP'}
                  </Button>
                </div>
              </form>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Login;
